import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '../../Servicios/supabaseClient.js';
import { Button, Card, Field, Icon, StatusPill } from '../../Components/ui/ui.jsx';
import { downloadExport } from './storage.js';
import { exportTone, formatWhen } from './format.js';

// Exportar ventas (comprobantes emitidos en ARCA) al formato RG 3685:
// VENTAS.txt + ALICUOTAS.txt (el mismo que "Exportar Ventas" de RCEL) + CSV.

const FILES = [
    { name: 'VENTAS.txt', desc: 'Cabecera de cada comprobante, en el formato de la RG 3685.' },
    { name: 'ALICUOTAS.txt', desc: 'El IVA de cada comprobante, separado por alícuota.' },
    { name: 'ventas.csv', desc: 'Los mismos datos para abrir en Excel o Google Sheets.' },
];

const isoDate = (d) => d.toISOString().slice(0, 10);

function fmtRange(r) {
    const f = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return `${f(r.desde)} – ${f(r.hasta)}`;
}

export default function Exportar() {
    const { user, profile, notify } = useOutletContext();

    const today = new Date();
    const [desde, setDesde] = useState(isoDate(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [hasta, setHasta] = useState(isoDate(today));
    const [ptosVenta, setPtosVenta] = useState(''); // "2, 4" (vacío = todos)
    // A ARCA se entra con el CUIT de la persona; si es sociedad, además el de la empresa
    const [cuit, setCuit] = useState('');
    const [cuitEmpresa, setCuitEmpresa] = useState('');
    const [claveFiscal, setClaveFiscal] = useState(''); // solo en memoria, nunca se guarda
    const [generating, setGenerating] = useState(false);
    const [exports, setExports] = useState([]);
    const [loading, setLoading] = useState(true);
    const pollTimer = useRef(null);

    const fetchExports = useCallback(async () => {
        const { data, error } = await supabase
            .from('sales_exports')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(10);
        if (!error) setExports(data || []);
        setLoading(false);
    }, [user]);

    // Precarga desde las credenciales ARCA guardadas (o el CUIT del perfil)
    useEffect(() => {
        let cancelled = false;
        supabase
            .from('afip_credentials')
            .select('cuit, cuit_empresa')
            .eq('user_id', user.id)
            .maybeSingle()
            .then(({ data }) => {
                if (cancelled) return;
                if (data?.cuit) {
                    setCuit(data.cuit);
                    setCuitEmpresa(data.cuit_empresa || '');
                } else if (profile?.cuit) {
                    setCuit(profile.cuit);
                }
            });
        return () => { cancelled = true; };
    }, [user, profile?.cuit]);

    useEffect(() => {
        fetchExports();
        const channel = supabase
            .channel('dashboard:sales_exports')
            .on('postgres_changes',
                { event: '*', schema: 'public', table: 'sales_exports', filter: `user_id=eq.${user.id}` },
                () => fetchExports())
            .subscribe();
        return () => {
            supabase.removeChannel(channel);
            clearTimeout(pollTimer.current);
        };
    }, [user, fetchExports]);

    // Si la función devolvió "procesando", seguimos consultando hasta que termine
    const pollCheck = async (exportId, attempt = 0) => {
        if (attempt > 30) {
            setGenerating(false);
            notify('El export sigue en proceso. Va a aparecer en la lista cuando termine.', 'warn');
            return;
        }
        const { data, error } = await supabase.functions.invoke('sales-export', {
            body: { action: 'check', exportId },
        });
        if (error || data?.status === 'error') {
            setGenerating(false);
            notify(data?.error || error?.message || 'Falló el export.', 'danger');
            return;
        }
        if (data?.status === 'completado') {
            setGenerating(false);
            fetchExports();
            notify('Export listo. Ya lo podés descargar.');
            return;
        }
        pollTimer.current = setTimeout(() => pollCheck(exportId, attempt + 1), 8000);
    };

    const handleGenerate = async (e) => {
        e.preventDefault();
        setGenerating(true);
        try {
            const ptos = ptosVenta
                .split(',')
                .map((s) => parseInt(s.trim(), 10))
                .filter((n) => Number.isInteger(n) && n > 0);

            const { data, error } = await supabase.functions.invoke('sales-export', {
                body: {
                    action: 'start',
                    desde,
                    hasta,
                    cuit: cuit.replace(/\D/g, ''),
                    cuitEmpresa: cuitEmpresa.replace(/\D/g, ''),
                    claveFiscal,
                    ...(ptos.length > 0 ? { ptosVenta: ptos } : {}),
                },
            });

            if (error) throw new Error(error.message);
            if (data?.error) throw new Error(data.error);

            if (data?.status === 'procesando') {
                fetchExports();
                pollTimer.current = setTimeout(() => pollCheck(data.exportId), 8000);
                return;
            }

            setGenerating(false);
            fetchExports();
            if (data?.warnings?.length > 0) {
                notify(`Export generado con avisos: ${data.warnings.join(' · ')}`, 'warn');
            } else {
                notify('Export listo. Ya lo podés descargar.');
            }
        } catch (err) {
            setGenerating(false);
            notify(err.message, 'danger');
        }
    };

    return (
        <div className="page">
            <header className="pageHeaderText">
                <span className="overline">RG 3685 · Libro IVA Digital</span>
                <h1 className="pageTitle">Exportar ventas</h1>
                <p className="pageSubtitle">
                    Bajamos de ARCA los comprobantes que emitiste en el período y armamos los mismos archivos que "Exportar ventas" de Comprobantes en Línea.
                </p>
            </header>

            <div className="split">
                <Card title="Nuevo export" featured>
                    <form className="form" onSubmit={handleGenerate}>
                        <div className="grid3">
                            <Field id="exp-desde" label="Desde" type="date" value={desde} max={hasta} required onChange={(e) => setDesde(e.target.value)} />
                            <Field id="exp-hasta" label="Hasta" type="date" value={hasta} min={desde} required onChange={(e) => setHasta(e.target.value)} />
                            <Field
                                id="exp-ptos"
                                label="Puntos de venta (opcional)"
                                placeholder="2, 4"
                                hint="Vacío = todos los puntos de venta."
                                value={ptosVenta}
                                onChange={(e) => setPtosVenta(e.target.value)}
                            />
                        </div>
                        <div className="grid3">
                            <Field
                                id="exp-cuit-empresa"
                                label="CUIT de la empresa (opcional)"
                                placeholder="30123456789"
                                inputMode="numeric"
                                hint="Solo si sos sociedad."
                                value={cuitEmpresa}
                                onChange={(e) => setCuitEmpresa(e.target.value)}
                            />
                            <Field
                                id="exp-cuit"
                                label={cuitEmpresa ? 'CUIT del representante' : 'CUIT'}
                                placeholder="20123456789"
                                inputMode="numeric"
                                hint="Con el que entrás a ARCA."
                                required
                                value={cuit}
                                onChange={(e) => setCuit(e.target.value)}
                            />
                            <Field
                                id="exp-clave"
                                label="Clave fiscal"
                                type="password"
                                autoComplete="off"
                                placeholder="••••••••"
                                hint="Solo para esta consulta. No la guardamos."
                                required
                                value={claveFiscal}
                                onChange={(e) => setClaveFiscal(e.target.value)}
                            />
                        </div>
                        <div className="formActions">
                            <Button type="submit" variant="primary" disabled={generating}>
                                {generating ? 'Generando…' : 'Generar export'}
                            </Button>
                        </div>
                    </form>
                    <div className="noteBox">
                        <Icon name="info" size={16} style={{ color: 'var(--accent)' }} />
                        Si el portal de ARCA está lento, reintentamos solos. Un mes completo suele tardar unos minutos.
                    </div>
                </Card>

                <Card>
                    <h2 className="h3">Qué vas a descargar</h2>
                    <p className="cardText">Un ZIP con tres archivos:</p>
                    {FILES.map((f) => (
                        <div key={f.name} className="fileItem">
                            <span className="fileIcon"><Icon name="file" /></span>
                            <div className="cellStack">
                                <span className="fileName">{f.name}</span>
                                <span className="cardText">{f.desc}</span>
                            </div>
                        </div>
                    ))}
                </Card>
            </div>

            <Card title="Exports anteriores">
                <div className="tableWrap">
                    <table className="dataTable">
                        <thead>
                            <tr>
                                <th>Generado</th>
                                <th>Período</th>
                                <th>Ptos. de venta</th>
                                <th className="right">Comprobantes</th>
                                <th>Estado</th>
                                <th><span className="srOnly">Acción</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {exports.length === 0 ? (
                                <tr><td colSpan="6" className="emptyRow">{loading ? 'Cargando…' : 'Todavía no generaste ningún export.'}</td></tr>
                            ) : exports.map((r) => {
                                const st = exportTone(r.status);
                                const withWarnings = r.status === 'completado' && r.error;
                                return (
                                    <tr key={r.id}>
                                        <td className="muted num nowrap">{formatWhen(r.created_at)}</td>
                                        <td className="num nowrap strong">{fmtRange(r)}</td>
                                        <td>{r.ptos_venta?.length ? r.ptos_venta.join(', ') : 'Todos'}</td>
                                        <td className="right num">{r.comprobantes_count ?? '-'}</td>
                                        <td>
                                            <StatusPill tone={withWarnings ? 'warn' : st.tone} title={r.error || undefined}>
                                                {withWarnings ? 'Con avisos' : st.label}
                                            </StatusPill>
                                        </td>
                                        <td>
                                            <div className="cellActions">
                                                {r.status === 'completado' ? (
                                                    <Button size="sm" onClick={() => downloadExport(r.file_path, notify)}>Descargar ZIP</Button>
                                                ) : r.status === 'error' ? (
                                                    <span className="cellSub" style={{ color: 'var(--danger)' }} title={r.error || undefined}>Falló</span>
                                                ) : (
                                                    <span className="cellSub">Generando…</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}
